import { ActionContext } from 'vuex';
import { ICustomFields, ILanguages, IPanels, ITranslations } from '@llws/lift-entity-interfaces';
import {
  cloneObject,
  IApiResponse,
  IFilterPayload,
  IModifyPayload,
  subsetObject,
} from '@/shared';
import { ICustomContentResponse, ICustomContentStoreState, ICustomFieldsEditable, IPanelTemplateWithFields, IPanelWithFields } from '@/shared/interfaces/custom-content-store';
import customContent from '@/shared/services/custom-content';
import { ICustomFieldModifyPayload } from '@/shared/interfaces/custom-field-modify-payload';
import { DataModifyProcessor } from '@/shared/classes/data-modify-processor';
import config from '@/config';
import { adminRequest } from '@/shared/request-configs/admin-request';
import { getTranslationsByClientIdAndModel, saveTranslations } from '@/shared/services/translations';
import { getNewTranslation } from '@/shared/factory/new-translation';
import { IPanelTemplateGroupWithPanels } from '@/shared/interfaces/panel-template-group-with-panels';
import { getExistingFields, getPanelsByTemplateGroup } from '@/shared/helpers/panel-helpers';
import app from '@/main';
import { TRootState } from '../index';
import i18n from '@/i18n';

const initialState: ICustomContentStoreState = {
  list: {
    data: {
      customFields: [],
      customFieldTemplates: [],
      panels: [],
      panelTemplates: [],
      siteCustomContent: {
        childCustomFields: [],
        childCustomFieldTemplates: [],
        childPanels: [],
        childPanelTemplates: [],
      },
    },
    meta: null,
  },
  customFieldTranslations: [],
};
interface editPayload {
  data: ICustomFields[],
  isChild: boolean
}

function sortCustomFields(a: ICustomFieldsEditable, b: ICustomFieldsEditable): number {
  if (a.orderBy > b.orderBy) {
    return 1;
  }

  if (a.orderBy < b.orderBy) {
    return -1;
  }

  if ((a.label || '') < (b.label || '')) {
    return -1;
  }

  if ((a.label || '') > (b.label || '')) {
    return 1;
  }

  return 0;
}

export default {
  namespaced: true, // 'custom-content/'
  state: initialState,

  getters: {
    customFieldTemplates(state: ICustomContentStoreState) {
      return state.list?.data?.customFieldTemplates ?? [];
    },
    customFields: (
      state: ICustomContentStoreState,
    ) => (lang: string = 'en') => ([
      ...state.list.data.customFields,
      ...state.list.data.customFieldTemplates
        .filter((v) => !(state.list?.data.customFields || []).some((va) => va.key === v.key))
        .map((v) => ({ ...v, value: '' })),
    ]).map((cf) => ({
      ...cf,
      value: lang === 'en'
        ? cf.value
        : (state.customFieldTranslations).find((cft) => `${cft.foreignId}` === `${cf.id}`)?.value || '',
      options: state.list!.data.customFieldTemplates.find((v) => v.key === cf.key)?.options || '',
      template: state.list!.data.customFieldTemplates.find((v) => v.key === cf.key) || cloneObject(cf),
    })).sort(sortCustomFields),

    childCustomFields: (state: ICustomContentStoreState) => (lang: string = 'en') => ([
      ...(state.list.data.siteCustomContent?.childCustomFields ?? []),
      ...(state.list.data.siteCustomContent?.childCustomFieldTemplates ?? [])
        .filter((v) => !(state.list?.data.siteCustomContent?.childCustomFields || []).some((va) => va.key === v.key && v.clientId === va.clientId))
        .map((v) => ({ ...v, value: '' })),
    ]).map((cf) => ({
      ...cf,
      value: lang === 'en'
        ? cf.value
        : (state.customFieldTranslations).find((cft) => `${cft.foreignId}` === `${cf.id}`)?.value || '',
      options: state.list!.data.siteCustomContent.childCustomFieldTemplates.find((v) => v.key === cf.key)?.options || '',
      template: state.list!.data.siteCustomContent.childCustomFieldTemplates.find((v) => v.key === cf.key) || cloneObject(cf),
    })).sort(sortCustomFields),

    panelsByTemplate(state: ICustomContentStoreState) {
      if (state.list?.data) {
        const templates = Array.from(state.list.data.panelTemplates);
        const panels = Array.from(state.list.data.panels);
        templates.forEach((t, i) => { templates[i].panels = []; });

        panels.forEach((p) => {
          const templateIdx = templates.findIndex((t) => t.key === p.panelTemplateKey);

          if (templateIdx !== -1) {
            const template = templates[templateIdx];
            templates[templateIdx].panels!.push({ ...p, fields: getExistingFields(p, template) });
          }
        });

        templates.forEach((t, i) => {
          templates[i].panels = t.panels?.sort((a, b) => (a.order > b.order ? 1 : -1));
        });

        return templates;
      }

      return [];
    },

    filterTemplates(templates: IPanelTemplateWithFields[], panels: IPanelWithFields[]): IPanelTemplateGroupWithPanels[] {
      return templates
        .map((v) => ({
          groupName: v.panelTemplateGroups?.groupName || i18n.t('website.posts.content.content') || 'Content',
          groupKey: v.panelTemplateGroups?.groupKey || null,
          groupOrder: v.panelTemplateGroups?.tabOrder || 0,
          panelTemplates: templates.filter((pt) => pt.templateGroupKey === (v.panelTemplateGroups?.groupKey || null)),
          panels: getPanelsByTemplateGroup(v.panelTemplateGroups?.groupKey, panels, templates),
        }))
        .filter((v, i, a) => a.findIndex((v1) => v1.groupKey === v.groupKey) === i)
        .sort((a, b) => {
          if (!b.groupKey) return 1;
          return (((a.groupKey || '') > (b.groupKey || '') ? 1 : -1) && ((a.groupOrder || 0) > (b.groupOrder || 0) ? 1 : -1));
        }) as IPanelTemplateGroupWithPanels[];
    },

    panelsByTemplateGroup(state: ICustomContentStoreState): IPanelTemplateGroupWithPanels[] {
      const templates = Array.from(state.list?.data.panelTemplates || []);
      const panels = Array.from(state.list?.data.panels || []);
      if (templates.length && this) {
        return this.filterTemplates(templates, panels);
      }
      return [] as IPanelTemplateGroupWithPanels[];
    },

    childPanelsByTemplateGroup(state: ICustomContentStoreState): IPanelTemplateGroupWithPanels[] {
      const templates = Array.from(state.list?.data.siteCustomContent?.childPanelTemplates || []);
      const panels = Array.from(state.list?.data.siteCustomContent?.childPanels || []);
      if (templates.length) {
        return this.filterTemplates(templates, panels);
      }
      return [] as IPanelTemplateGroupWithPanels[];
    },

    childCustomFieldsWithValue(state: ICustomContentStoreState) {
      return state.list?.data?.siteCustomContent
        ? [ ...state.list.data.siteCustomContent.childCustomFields, ...state.list.data.siteCustomContent.childCustomFieldTemplates ]
        : [];
    },

    customFieldsWithValue(state: ICustomContentStoreState) {
      return state.list?.data ? [ ...state.list.data.customFields, ...state.list.data.customFieldTemplates ] : [];
    },

    panels(state: ICustomContentStoreState) {
      return state.list?.data ? [
        ...state.list.data.panels,
        ...(state.list.data.siteCustomContent?.childPanels ?? []),
      ] : [];
    },

    siteCustomContent(state: ICustomContentStoreState) {
      return state.list?.data?.siteCustomContent ? { ...state.list?.data?.siteCustomContent } : {};
    },
  },
  mutations: {
    set: (
      state: any,
      payload: IApiResponse<ICustomContentResponse, null> | ICustomContentResponse,
    ) => {
      state.list = (payload as any).data
        ? payload as IApiResponse<ICustomContentResponse, null>
        : { data: payload, meta: state.list.meta };
    },

    reset: (state: ICustomContentStoreState) => {
      state.list = initialState.list;
      state.customFieldTranslations = [];
    },

    setCustomFieldsWithValue(state: ICustomContentStoreState, payload: editPayload) {
      if (state.list?.data) {
        const customFields: ICustomFieldsEditable[] = [];
        const customFieldsTemplates: ICustomFieldsEditable[] = [];

        payload.data.forEach((x) => {
          if (x.identifierId) {
            customFields.push(x);
          } else {
            customFieldsTemplates.push(x);
          }
        });

        if (payload.isChild) {
          state.list.data.siteCustomContent.childCustomFields = customFields;
          state.list.data.siteCustomContent.childCustomFieldTemplates = customFieldsTemplates;
        } else {
          state.list.data.customFieldTemplates = customFieldsTemplates;
          state.list.data.customFields = customFields;
        }
      }
    },

    setPanels(state: ICustomContentStoreState, payload: IPanelWithFields[]) {
      if (state.list?.data) {
        state.list.data.panels = payload;
      }
    },

    setCustomFieldTranslations(state: ICustomContentStoreState, payload: ITranslations[]) {
      state.customFieldTranslations = payload;
    },
  },
  actions: {
    get: async (ctx: ActionContext<ICustomContentStoreState, any>, payload: IFilterPayload) => {
      if (ctx.rootState.language === 'en') {
        const result = await customContent.get(
          payload.id!,
          payload.additional!.tableName,
          payload.additional!.modelType,
          payload.additional!.parent,
          payload.additional!.clientId,
          payload.additional!.customFieldCategories,
          payload.additional!.siteIds,
          payload.additional!.siteClientIds,
        );
        ctx.commit('set', result);

        return result;
      }

      return null;
    },

    saveCustomFields: async (ctx: ActionContext<ICustomContentStoreState, TRootState>, payload: ICustomFieldModifyPayload) => {
      if (ctx.rootState.language !== 'en' || payload.language?.languageCode) {
        ctx.dispatch('saveTranslatedCustomFields', payload);
      } else {
        const url = `${config.adminGateway}/property-api/custom-fields?tableName=${payload.tableName}`;
        const processor = new DataModifyProcessor<ICustomFields>(url);

        const { postItems, putItems } = payload.data.reduce((acc, cur) => {
          /* Custom Field has ID and is either a template or being edited by regular user */

          if (cur.data.id && (!cur.data.template || cur.data.identifierId)) {
            acc.putItems = [ ...acc.putItems, subsetObject(cur.data, ['template']) ];
          } else {
            acc.postItems = [
              ...acc.postItems,
              /* New custom field template for a specific model (identifierId is null) */
              ...!cur.data.id && !cur.data.identifierId ? [ subsetObject(cur.data, ['template', 'id', 'value']) ] : [],
              /* Custom field drawn from template or scoped to model only */
              { ...subsetObject(cur.data, ['template', 'id']), identifierId: payload.identifierId },
            ];
          }

          return acc;
        }, {
          postItems: [] as ICustomFields[],
          putItems: [] as ICustomFields[],
        });

        processor.postItems = postItems.map((cf) => ({ ...cf, tableName: payload.tableName }));
        processor.putItems = putItems;

        const result: { modifiedResultSet: ICustomFields[] } = await processor.execute();
        const current: ICustomContentResponse = { ...ctx.state.list! };

        ctx.commit('set', (result.modifiedResultSet).reduce((acc, cur) => {
          if (cur.identifierId) {
            if (acc.data.siteCustomContent?.childCustomFields) {
              acc.data.siteCustomContent.childCustomFields = [
                ...acc.data.siteCustomContent.childCustomFields.filter((v) => v.id !== cur.id),
                ...[cur],
              ];
            }

            // We need to keep in mind that could also be an owned website...
            if (cur.clientId === app()?.$rsGlobals.$currentClient.id
              || app()?.$rsGlobals.$currentClient.ownedSites?.find((s) => s.sourceClientId === cur.clientId)) {
              acc.data.customFields = [
                ...acc.data.customFields.filter((v) => v.id !== cur.id),
                ...[cur],
              ];
            }
          } else {
            if (acc.data.siteCustomContent?.childCustomFieldTemplates) {
              acc.data.siteCustomContent.childCustomFieldTemplates = [
                ...acc.data.siteCustomContent.childCustomFieldTemplates.filter((v) => v.id !== cur.id),
                ...[cur],
              ];
            }

            if (cur.clientId === app()?.$rsGlobals.$currentClient.id
              || app()?.$rsGlobals.$currentClient.ownedSites?.find((s) => s.sourceClientId === cur.clientId)) {
              acc.data.customFieldTemplates = [
                ...acc.data.customFieldTemplates.filter((v) => v.id !== cur.id),
                ...[cur],
              ];
            }
          }

          return acc;
        }, current));
      }
    },

    saveTranslatedCustomFields: async (
      ctx: ActionContext<ICustomContentStoreState, any>,
      payload: ICustomFieldModifyPayload,
    ) => {
      const fieldsToProcess = payload.data.filter((v) => v.data.identifierId);
      const translations = cloneObject(ctx.state.customFieldTranslations);

      fieldsToProcess.forEach((f) => {
        const tIdx = translations.findIndex((t) => `${t.foreignId}` === `${f.data.id}`);
        if (tIdx !== -1) {
          translations[tIdx].value = f.data.value || '';
        } else if (f.data.value) {
          const languageId = payload.language?.id || app()?.$rsGlobals.$siteSourceClientLanguages.find(
            (l: ILanguages) => ctx.rootState.language === l.languageCode,
          )?.id!;

          const newTranslation = getNewTranslation(
            payload.sourceId ? payload.sourceId : app()!.$rsGlobals.$siteSourceClientId!,
            f.data.id!,
            languageId,
            'value',
            'custom_fields',
          );
          newTranslation.value = f.data.value || '';
          translations.push(newTranslation);
        }
      });

      const result = await saveTranslations(translations);
      ctx.commit('setCustomFieldTranslations', result);
    },

    savePanels: async (ctx: ActionContext<ICustomContentStoreState, any>, payload: IModifyPayload): Promise<ICustomContentResponse> => {
      if (payload.data?.length) {
        const tableName = payload.data ? (payload.data as IPanels[])[0]?.model : '';
        const processor = new DataModifyProcessor<IPanelWithFields>(`${config.adminGateway}/property-api/panels?tableName=${tableName}`);

        payload.data.forEach((p: IPanelWithFields) => {
          if (!p.id) {
            processor.postItems.push(p);
          } else {
            processor.putItems.push(p);
          }
        });

        const result = await processor.execute();
        const current = { ...ctx.state.list } as ICustomContentResponse;

        if (result.putResult?.data) {
          // Add content to the sites panels if requested, otherwise regular panels
          if (payload.additional?.saveSiteContent) {
            current.data.siteCustomContent.childPanels = current.data.siteCustomContent.childPanels.filter(
              (p) => !result.putResult.data!.some((pm) => pm.id === p.id),
            );
            current.data.siteCustomContent.childPanels = [ ...current.data.siteCustomContent.childPanels, ...result.putResult.data ];
          } else {
            current.data.panels = current.data.panels.filter(
              (p) => !result.putResult.data!.some((pm) => pm.id === p.id),
            );
            current.data.panels = [ ...current.data.panels, ...result.putResult.data ];
          }
        }

        if (result.postResult?.data) {
          if (payload.additional?.saveSiteContent) {
            current.data.siteCustomContent.childPanels = [ ...current.data.siteCustomContent.childPanels, ...result.postResult.data ];
          } else {
            current.data.panels = [ ...current.data.panels, ...result.postResult.data ];
          }
        }
        ctx.commit('set', current);
        return current;
      }
      return { data: payload.data, meta: null } as ICustomContentResponse;
    },

    removeCustomFieldWithValue: async (ctx: ActionContext<ICustomContentStoreState, any>, payload: {
      id: number,
      tableName: string,
      setState?: boolean,
      isChild?: boolean,
    }) => {
      const current = { ...ctx.state.list } as ICustomContentResponse;
      const result: { data: ICustomFields[] } = await adminRequest.delete(
        `/property-api/custom-fields`,
        [payload.id],
        `tableName=${payload.tableName}`,
      ) as unknown as { data: ICustomFields [] };

      if (result.data[0].identifierId) {
        current.data.customFields = (current.data.customFields || [])
          .filter((c) => c.id !== payload.id);
        current.data.siteCustomContent.childCustomFields = (current.data.siteCustomContent.childCustomFields || [])
          .filter((c) => c.id !== payload.id);
      } else {
        current.data.customFieldTemplates = current.data.customFieldTemplates
          .filter((c) => c.id !== payload.id);
        current.data.siteCustomContent.childCustomFieldTemplates = current.data.siteCustomContent.childCustomFieldTemplates
          .filter((c) => c.id !== payload.id);
      }

      ctx.commit('set', current);
    },

    removePanel: async (ctx: ActionContext<ICustomContentStoreState, any>, payload: {
      id: number,
      tableName: string,
      setState?: boolean,
    }) => {
      const current = ctx.getters.panels;
      await adminRequest.delete(`/property-api/panels`, [payload.id], `tableName=${payload.tableName}`);
      ctx.commit('setPanels', current.filter((v: ICustomFields) => v.id !== payload.id));
    },

    getCustomFieldTranslations: async (
      ctx: ActionContext<ICustomContentStoreState, any>,
      payload: Record<string, any>,
    ) => {
      const foreignIds = Array.from((ctx.state.list?.data.customFields || []), (v: ICustomFields) => v.id!);
      const childCofreignIds = Array.from((ctx.state.list?.data.siteCustomContent.childCustomFields || []), (v: ICustomFields) => v.id!);

      const cfTranslations = await getTranslationsByClientIdAndModel(
        payload.clientId ? parseInt(`${payload.clientId}`, 10) : ctx.rootGetters['users/currentClient'].id,
        'custom_fields',
        [...foreignIds, ...childCofreignIds],
        true,
        payload.siteClientIds,
        payload.language,
      );
      ctx.commit('setCustomFieldTranslations', cfTranslations);
    },
  },
};
